import './ui/styles.css';
import { sfxListener } from './game/audio-wiring';
import { createGame } from './game/game';
import { createAnimationFrameLoop } from './game/loop';
import { ScreenFlow } from './game/screen-flow';
import type { TestHooks } from './game/test-hooks';
import { browserAudioContext, createAudioEngine } from './audio/engine';
import { createSfxPlayer } from './audio/sfx';
import { bindAudioUnlock } from './audio/unlock';
import { bindDomInput } from './input/dom-input';
import { createLevelScene, type LevelScene } from './render/level-scene';
import { createTitleScene } from './render/title-scene';
import { t } from './i18n/es';
import { initStorage } from './save/storage';
import { buildId, createTelemetry } from './game/telemetry';
import { mountFpsCounter } from './ui/fps';
import { showNotice } from './ui/notices';
import { mountRotateHint } from './ui/rotate-hint';
import { mountScreens } from './ui/screens';

declare global {
  interface Window {
    __NARCOVERSE__?: TestHooks;
  }
}

const stage = document.querySelector<HTMLElement>('#stage')!;
const ui = document.querySelector<HTMLElement>('#ui')!;
const app = document.querySelector<HTMLElement>('#app')!;
const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

// GDD §6.4: if the browser refuses to persist, play in memory and say so permanently. Reading
// localStorage can itself throw (SecurityError); initStorage turns that into memory mode.
const storage = initStorage(
  () => window.localStorage,
  () => showNotice(app, t('ui.save.unavailable')),
);

const flow = new ScreenFlow();

// Local playtesting telemetry (issue #144): off by default, in memory, with optional sessionStorage. It is
// enabled from the test hooks (or ?telemetry=1 in hook builds) and never leaves the browser.
const telemetry = createTelemetry({
  now: () => Date.now(),
  build: buildId,
  storage: (() => {
    try {
      const store = window.sessionStorage;
      return { get: (key: string) => store.getItem(key), set: (key: string, value: string) => store.setItem(key, value) };
    } catch {
      return undefined;
    }
  })(),
});

// Settings that act on the interface (GDD §13.6, #63): the FPS counter and the motion of the interface.
const fps = mountFpsCounter(app);
const applyInterfaceSettings = (settings: { reducedMotion: boolean; showFps: boolean }) => {
  document.documentElement.dataset.reducedMotion = String(settings.reducedMotion);
  fps.setVisible(settings.showFps);
};
const hitboxDebug = new URLSearchParams(window.location.search).get('debug') === 'hitbox';

// The level scene replaces the title backdrop when a level is loaded (issue #53). The loop below draws it
// with the interpolation factor of the fixed-step loop; the DOM input flaps the plane through game.actions.
let levelScene: LevelScene | null = null;
let audio: ReturnType<typeof createAudioEngine> | null = null;
let sfx: ReturnType<typeof createSfxPlayer> | null = null;
let unbindInput = () => {};
// Fixed-step simulation at 60 Hz (GDD §15.4); it only acts once a level is loaded. PAUSA freezes it (#60).
const frames = createAnimationFrameLoop({
  onStep: () => game.step(),
  // Paused, the loop freezes alpha; the scene then shows the current state (hooks render frames with alpha 1).
  onRender: (alpha) => levelScene?.render(frames.loop.isPaused ? 1 : alpha),
});
const game = createGame({
  loop: frames.loop,
  adapter: storage.adapter,
  flow,
  now: () => new Date().toISOString(),
  prefersReducedMotion: reducedMotion,
  newSeed: () => Date.now() >>> 0,
  onNotice: (text) => showNotice(app, text),
  telemetry,
  onSettings: (settings) => {
    audio?.applyVolumes(settings);
    applyInterfaceSettings(settings);
  },
  onBeforeStep: () => levelScene?.beforeStep(),
  onAfterStep: () => levelScene?.afterStep(),
  onRun: (run) => {
    // The sound effects follow the events of the core (issue #76).
    if (sfx) {
      sfx.clearLog();
      run.core.listeners.push(sfxListener(sfx));
    }
    scene?.dispose();
    scene = null;
    unbindInput();
    levelScene?.dispose();
    levelScene = createLevelScene(stage, run, {
      hitboxes: import.meta.env.MODE !== 'production' && hitboxDebug,
      reducedMotion: () => game.store.save.settings.reducedMotion,
    });
    unbindInput = bindDomInput({ enqueue: (action) => game.input(action) }, levelScene.canvas);
    levelScene.render(1);
  },
});

// Audio (GDD §14.3, issue #75): starts with the first interaction, follows the saved volumes and pauses with
// the game. Without Web Audio the engine reports `unavailable` and everything is a no-op.
applyInterfaceSettings(game.store.save.settings);
audio = createAudioEngine({ createContext: browserAudioContext, volumes: game.store.save.settings });
sfx = createSfxPlayer(audio);
bindAudioUnlock(audio);
let screenBefore = flow.current;
flow.subscribe((screen) => {
  if (screen === 'PAUSA') void audio?.suspend('pause');
  else if (screenBefore === 'PAUSA') void audio?.resume();
  screenBefore = screen;
});
// A hidden page suspends the audio; only an interaction (or RESUME) brings it back (GDD §5.5).
document.addEventListener('visibilitychange', () => {
  if (document.hidden) void audio?.suspend('hidden');
});


frames.start();

let scene: ReturnType<typeof createTitleScene> | null = createTitleScene(stage, { reducedMotion });
scene.start();
mountScreens(ui, flow, { store: () => game.store, deploy: (levelId) => game.deploy(levelId), flight: game, run: () => game.run, recount: () => game.recount, updateSetting: (key, value) => game.updateSetting(key, value) });
mountRotateHint(app);
// Nothing is loaded asynchronously yet: ARRANQUE ends right away (GDD §13.2).
flow.send('ASSETS_LOADED');

// GDD §15.5: test hooks only outside production builds; the dynamic import keeps the module out of the
// production bundle. `?test=1` adds a console trace of every hook call in those builds.
if (import.meta.env.MODE !== 'production') {
  const trace = new URLSearchParams(window.location.search).has('test')
    ? (message: string) => console.debug(`[__NARCOVERSE__] ${message}`)
    : undefined;
  // `?telemetry=1` turns the local playtesting telemetry on at boot (issue #144).
  if (new URLSearchParams(window.location.search).has('telemetry')) telemetry.enable();
  void import('./game/test-hooks').then(({ createTestHooks }) => {
    window.__NARCOVERSE__ = createTestHooks(game, frames.loop, trace, () => levelScene ?? scene, () => (audio && sfx ? { engine: audio, log: sfx.log } : null), telemetry);
  });
  // GDD §15.5 (proposal): the H key toggles the hitbox overlay in these builds.
  window.addEventListener('keydown', (event) => {
    if (event.code === 'KeyH' && levelScene) levelScene.setHitboxes(!levelScene.hitboxesOn);
  });
}

if (import.meta.hot) {
  import.meta.hot.dispose(() => {
    frames.stop();
    scene?.dispose();
    levelScene?.dispose();
  });
}
